import { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Alert,
  Box,
  Tab,
  Tabs,
  MenuItem,
  TextField,
} from "@mui/material";
import type { NodeDef } from "./workflow.types";

interface NewWorkflowDialogProps {
  open: boolean;
  triggerOptions: NodeDef[];
  isSubmitting: boolean;
  onCancel: () => void;
  onCreate: (payload: { name: string; description?: string; trigger: { type: string } }) => void;
  onGenerate: (request: string) => void;
}

export default function NewWorkflowDialog({
  open,
  triggerOptions,
  isSubmitting,
  onCancel,
  onCreate,
  onGenerate,
}: NewWorkflowDialogProps) {
  const [mode, setMode] = useState<'ai' | 'manual'>('ai');
  const [request, setRequest] = useState('');
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [triggerType, setTriggerType] = useState("manual");

  useEffect(() => {
    if (open) {
      setName("");
      setDescription("");
      setTriggerType(triggerOptions[0]?.subtype || "manual");
      setMode('ai');
      setRequest('');
    }
  }, [open, triggerOptions]);

  const canCreate = name.trim().length > 0;

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>Create a workflow</DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "8px !important" }}>
        <Tabs value={mode} onChange={(_, value) => setMode(value)} aria-label="Workflow creation method">
          <Tab value="ai" label="Build with AI" />
          <Tab value="manual" label="Set up manually" />
        </Tabs>
        {mode === 'ai' ? (
          <>
            <Alert severity="info">
              Describe the outcome. The agent uses the same workflow-safe tools and skill fields shown in the canvas.
            </Alert>
            <TextField
              autoFocus
              label="What should this workflow do?"
              fullWidth
              multiline
              minRows={5}
              value={request}
              onChange={(event) => setRequest(event.target.value)}
              placeholder="When a meeting is completed, generate a file note, create a goals list using our client-writing skill, then draft a follow-up email."
              helperText="The workflow is created inactive so you can review and test it first."
            />
          </>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <TextField
          autoFocus
          label="Name"
          fullWidth
          size="small"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <TextField
          label="Description (optional)"
          fullWidth
          size="small"
          multiline
          minRows={2}
          placeholder="What does this workflow do, and why — a note for anyone else who opens it later"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
        <TextField
          select
          label="Trigger"
          fullWidth
          size="small"
          value={triggerType}
          onChange={(e) => setTriggerType(e.target.value)}
        >
          {triggerOptions.map((def) => (
            <MenuItem key={def.subtype} value={def.subtype}>
              {def.label}
            </MenuItem>
          ))}
        </TextField>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onCancel} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={(mode === 'ai' ? request.trim().length < 10 : !canCreate) || isSubmitting}
          onClick={() => mode === 'ai'
            ? onGenerate(request.trim())
            : onCreate({ name: name.trim(), description: description.trim() || undefined, trigger: { type: triggerType } })}
        >
          {isSubmitting ? (mode === 'ai' ? 'Building…' : 'Creating…') : (mode === 'ai' ? 'Build workflow' : 'Create')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
